import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination } from "antd";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import FullScreenDialog from "../../../Components/FullScreenDialog";
import CloseButton from "../../../Components/CloseButton";
import CustomTag from "../../../Components/CustomTag";
import {
    CancelFormActionButton,
    PrimaryFormActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { getAsset, getAssetLifecycle } from "../../../Api/AssetApi";
import CODE from "../../../Static/Constants/StatusCodes";
import {
    buildLifecycleRows,
    getAssetDetailFields,
    getBadgeTheme,
} from "./assetTableHelpers";
import { FORM_TOKENS } from "../WorkOrders/WorkOrderFormShared";

// Full-page detail for one asset: what the asset record says about itself,
// then where it has actually been.
//
// THE TWO HALVES ARE DIFFERENT DATA AND ARE READ FROM DIFFERENT PLACES.
//
//   The card    -- assetMeta, including currentStateCode / currentEntityID /
//                  receiptCount, which are a CACHE the work order flow
//                  maintains.
//   The timeline -- assetLifecycleRecord, through GET /api/assets/<id>/lifecycle,
//                  which is the history itself.
//
// Neither is rendered from the other. The cache is what every list and filter
// in the product agrees on; the timeline is what actually happened. A drifted
// cache is a real failure mode, and showing the newest timeline row as "current
// state" -- or the cached state as the newest movement -- would hide exactly
// the disagreement this page exists to make visible.
//
// FullScreenDialog, not a route, for the same reasons as EntityDetailView: the
// back arrow, the scroll restore and the looks-like-a-page framing already
// behave the way users expect. A real route would also put the asset id in the
// URL, which is a separate decision about shareable links.

const LIFECYCLE_PAGE_SIZE = 10;

const SectionTitle = ({ children, action }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
        }}
    >
        <Typography
            sx={{
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: FORM_TOKENS.inkLabel,
            }}
        >
            {children}
        </Typography>
        {action}
    </Box>
);

const Card = ({ children }) => (
    <Box
        sx={{
            bgcolor: FORM_TOKENS.surface,
            border: `1px solid ${FORM_TOKENS.cardBorder}`,
            borderRadius: "10px",
            p: 3,
            mb: 3,
        }}
    >
        {children}
    </Box>
);

const Field = ({ label, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography
            sx={{ fontSize: "12px", lineHeight: "20px", color: FORM_TOKENS.inkLabel, mb: 0.25 }}
        >
            {label}
        </Typography>
        <Box
            sx={{
                fontSize: "14px",
                lineHeight: "22px",
                fontWeight: 500,
                color: FORM_TOKENS.ink,
                overflowWrap: "anywhere",
            }}
        >
            {children}
        </Box>
    </Box>
);

// The movement history, newest first, as the endpoint returns it.
//
// Paginated rather than scrolled: an asset that moves weekly has hundreds of
// rows, and pulling all of them to draw a timeline nobody reads to the bottom
// is a request the user pays for.
const LifecycleTimeline = ({
    rows,
    total,
    page,
    isLoading,
    errorMessage,
    onPageChange,
}) => {
    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (errorMessage) {
        return (
            <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.danger }}>
                {errorMessage}
            </Typography>
        );
    }

    if (!rows.length) {
        return (
            <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.inkFaint }}>
                This asset has never moved. It was registered outside a work order, so
                it has no state or custody yet.
            </Typography>
        );
    }

    return (
        <>
            <Box>
                {rows.map((row, index) => (
                    <Box
                        key={row.key}
                        data-testid={`asset-lifecycle-row-${row.key}`}
                        sx={{ display: "flex", gap: 2, minWidth: 0 }}
                    >
                        {/* The rail: a dot per movement, joined by a line that
                            stops at the last row so the timeline does not
                            appear to continue past what was loaded. */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                flexShrink: 0,
                                pt: 0.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: row.theme.fg,
                                }}
                            />
                            {index < rows.length - 1 && (
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        width: "1px",
                                        minHeight: 32,
                                        backgroundColor: FORM_TOKENS.fieldBorder,
                                        my: 0.5,
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ pb: index < rows.length - 1 ? 2.5 : 0, minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <CustomTag
                                    text={row.stateLabel}
                                    backgroundColor={row.theme.bg}
                                    fontColor={row.theme.fg}
                                    fontSize="12px"
                                    fontWeight="500"
                                />
                                <Typography
                                    sx={{ fontSize: "14px", fontWeight: 500, color: FORM_TOKENS.ink }}
                                >
                                    {row.entityName}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{ fontSize: "12px", color: FORM_TOKENS.inkFaint, mt: 0.25 }}
                            >
                                {/* effectiveFrom is the MOVEMENT's own clock,
                                    and createdAt is when it was recorded. A
                                    receipt entered three days late is true as
                                    of the receipt, so the two legitimately
                                    differ and the second is shown only when it
                                    adds something. */}
                                {row.effectiveFrom}
                                {row.recordedAt && row.recordedAt !== row.effectiveFrom
                                    ? ` · recorded ${row.recordedAt}`
                                    : ""}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            {total > LIFECYCLE_PAGE_SIZE && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Pagination
                        size="small"
                        showSizeChanger={false}
                        current={page}
                        pageSize={LIFECYCLE_PAGE_SIZE}
                        total={total}
                        onChange={onPageChange}
                        showTotal={(count, range) => `${range[0]}-${range[1]} / ${count}`}
                    />
                </Box>
            )}
        </>
    );
};

const AssetDetailView = ({ open, asset, onClose, onEdit }) => {
    // Seeded from the row so the page paints immediately, then replaced by the
    // detail response -- the row carries only the columns the table shows.
    const [detail, setDetail] = useState(asset);
    const [isLoading, setIsLoading] = useState(false);

    const [lifecycle, setLifecycle] = useState([]);
    const [lifecycleTotal, setLifecycleTotal] = useState(0);
    const [lifecyclePage, setLifecyclePage] = useState(1);
    const [lifecycleLoading, setLifecycleLoading] = useState(false);
    const [lifecycleError, setLifecycleError] = useState("");

    const assetID = asset?.id;

    useEffect(() => {
        setDetail(asset);
        // A new asset starts at the newest page of ITS history, not at
        // whatever page the previously opened asset was left on.
        setLifecyclePage(1);
        setLifecycle([]);
        setLifecycleTotal(0);
        setLifecycleError("");
    }, [asset]);

    useEffect(() => {
        if (!open || !assetID) return undefined;
        let cancelled = false;
        setIsLoading(true);
        (async () => {
            const res = await getAsset(assetID);
            if (cancelled) return;
            if (res?.responseStatus?.code === CODE.SUCCESS && res?.data) {
                setDetail(res.data);
            }
            // A failure leaves the row-seeded values on screen rather than
            // blanking a page the user is already reading.
            setIsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, assetID]);

    useEffect(() => {
        if (!open || !assetID) return undefined;
        let cancelled = false;
        setLifecycleLoading(true);
        setLifecycleError("");
        (async () => {
            const res = await getAssetLifecycle(assetID, {
                page: lifecyclePage,
                limit: LIFECYCLE_PAGE_SIZE,
            });
            if (cancelled) return;
            if (res?.responseStatus?.code === CODE.SUCCESS) {
                setLifecycle(res?.data?.records || []);
                setLifecycleTotal(res?.data?.pagination?.total || 0);
            } else {
                // Distinguished from "never moved": an empty timeline is a
                // statement about the asset, and a failed request is not.
                setLifecycle([]);
                setLifecycleTotal(0);
                setLifecycleError(
                    res?.responseStatus?.msg || "Could not load the movement history"
                );
            }
            setLifecycleLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, assetID, lifecyclePage]);

    const handleEdit = useCallback(() => onEdit?.(detail), [onEdit, detail]);

    const fields = useMemo(() => getAssetDetailFields(detail), [detail]);
    const lifecycleRows = useMemo(() => buildLifecycleRows(lifecycle), [lifecycle]);

    if (!asset) return null;

    return (
        <FullScreenDialog
            open={open}
            handleClose={onClose}
            containerStyles={{ bgcolor: FORM_TOKENS.panel }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: FORM_TOKENS.panel,
                    minHeight: "100vh",
                }}
            >
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        bgcolor: FORM_TOKENS.panel,
                        px: 3,
                        pt: 2,
                        pb: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                minWidth: 0,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={onClose}
                                data-testid="asset-detail-back"
                                aria-label="Back to the list"
                            >
                                <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                            <Typography
                                variant="fw700s20lh24c2E2E2E"
                                sx={{ overflowWrap: "anywhere" }}
                            >
                                {detail?.serialNumber || "Asset"}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: FORM_TOKENS.inkFaint,
                                    flexShrink: 0,
                                    ml: 1,
                                }}
                            >
                                {detail?.assetTypeCode || ""}
                            </Typography>
                            {isLoading && <CircularProgress size={14} sx={{ ml: 1.5 }} />}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {onEdit && (
                                <PrimaryFormActionButton
                                    data-testid="asset-detail-edit"
                                    onClick={handleEdit}
                                >
                                    Edit
                                </PrimaryFormActionButton>
                            )}
                            <CloseButton onClick={onClose} />
                        </Box>
                    </Box>

                    <Divider sx={{ mt: 2, borderColor: FORM_TOKENS.fieldBorder }} />
                </Box>

                <Box sx={{ px: 3, pt: 3, pb: 4 }}>
                    <Card>
                        <SectionTitle>Details</SectionTitle>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                    lg: "repeat(3, 1fr)",
                                },
                                columnGap: 4,
                                rowGap: 2.5,
                            }}
                        >
                            {fields.map((field) => (
                                <Field key={field.key} label={field.label}>
                                    {field.isState && field.value !== "-" ? (
                                        <CustomTag
                                            text={field.value}
                                            backgroundColor={
                                                getBadgeTheme(field.badgeColorTheme).bg
                                            }
                                            fontColor={getBadgeTheme(field.badgeColorTheme).fg}
                                            fontSize="12px"
                                            fontWeight="500"
                                        />
                                    ) : (
                                        field.value
                                    )}
                                </Field>
                            ))}
                        </Box>
                        <Typography
                            sx={{ fontSize: "12px", color: FORM_TOKENS.inkFaint, mt: 2.5, display: "block" }}
                        >
                            Current state and custody are maintained by the work
                            order flow and cannot be edited here.
                        </Typography>
                    </Card>

                    <Card>
                        <SectionTitle
                            action={
                                <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkFaint }}>
                                    {lifecycleTotal
                                        ? `${lifecycleTotal} movement${lifecycleTotal === 1 ? "" : "s"}`
                                        : ""}
                                </Typography>
                            }
                        >
                            Lifecycle Timeline
                        </SectionTitle>
                        <LifecycleTimeline
                            rows={lifecycleRows}
                            total={lifecycleTotal}
                            page={lifecyclePage}
                            isLoading={lifecycleLoading}
                            errorMessage={lifecycleError}
                            onPageChange={setLifecyclePage}
                        />
                    </Card>

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <CancelFormActionButton
                            data-testid="asset-detail-back-to-list"
                            onClick={onClose}
                        >
                            Back to list
                        </CancelFormActionButton>
                    </Box>
                </Box>
            </Box>
        </FullScreenDialog>
    );
};

export default AssetDetailView;
